"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import React, { forwardRef, useRef, useState } from "react";
import { AnimatedBeam } from "@/components/ui/animated-beam";
import { Server } from "lucide-react";
import { Icon } from "@/components/landing/Navbar";

const Circle = forwardRef<
  HTMLDivElement,
  { className?: string; children?: React.ReactNode; onClick?: () => void }
>(({ className, children, onClick }, ref) => {
  return (
    <div
      ref={ref}
      onClick={onClick}
      className={cn(
        "z-10 flex size-12 items-center justify-center rounded-full bg-accent-foreground p-3 text-accent",
        className
      )}
    >
      {children}
    </div>
  );
});

Circle.displayName = "Circle";

export function HeroIcons({ className, logo }: { className?: string; logo?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const div1Ref = useRef<HTMLDivElement>(null);
  const div2Ref = useRef<HTMLDivElement>(null);
  const div3Ref = useRef<HTMLDivElement>(null);

  const logos = [
    "/assets/app/nextjs.png",
    "/assets/app/docker.png",
    "/assets/app/java.svg",
    "/assets/app/react.png"
  ];

  const [logoIndex, setLogoIndex] = useState(0);

  const handleLogoClick = () => {
    setLogoIndex(prevIndex => (prevIndex + 1) % logos.length);
  };

  const currentLogo = logo ?? logos[logoIndex];

  return (
    <div
      className={cn(
        "relative flex h-[60px] w-full items-center justify-center overflow-hidden pr-10",
        className
      )}
      ref={containerRef}
    >
      <div className="flex size-full max-w-lg flex-row items-stretch justify-between gap-10">
        <div className="flex flex-col justify-center gap-2">
          <Circle
            ref={div1Ref}
            onClick={handleLogoClick}
            className="cursor-pointer user-select-none"
          >
            <img
              src={currentLogo}
              alt="Your app"
              width={24}
              height={24}
              className="rounded-full transition-opacity duration-500 h-6 w-6 light:invert cursor-pointer"
            />
          </Circle>
        </div>
        <div className="flex flex-col justify-center">
          <Circle ref={div2Ref}>
            <Link
              href="/dashboard"
              className="group flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full text-lg font-semibold text-primary-foreground md:h-8 md:w-8"
              title="DollarDeploy"
            >
              <Icon
                light
                className="h-6 w-6 mt-0.5 mr-0.5 dark:invert-0 transition-all group-hover:scale-[1.1]"
              />
            </Link>
          </Circle>
        </div>
        <div className="flex flex-col justify-center">
          <Circle ref={div3Ref}>
            <Server />
          </Circle>
        </div>
      </div>

      <AnimatedBeam
        duration={3}
        pathWidth={4}
        containerRef={containerRef}
        fromRef={div1Ref}
        toRef={div2Ref}
      />

      <AnimatedBeam
        duration={3}
        pathWidth={4}
        containerRef={containerRef}
        fromRef={div2Ref}
        toRef={div3Ref}
      />
    </div>
  );
}

export default HeroIcons;
